/**
 * Empresas prestadoras — quem executa obras no Dozy Business.
 *
 * `empresas` nasceu do lado do cliente: a organização que encomenda. Mas no
 * Business quem se candidata são empresas, e a mesma entidade pode estar dos
 * dois lados — uma construtora encomenda obras e executa obras. Por isso não
 * há aqui uma segunda tabela: há uma bandeira, e esta página é a vista das
 * empresas que a têm levantada.
 *
 * Três coisas decidem se uma empresa recebe convites, e estão todas neste
 * ecrã porque falhar uma delas produz o mesmo sintoma — silêncio:
 *
 *   1. estado 'active' (aprovada);
 *   2. pelo menos uma capacidade verificada, com tecto;
 *   3. pelo menos um membro, que é quem responde em nome dela.
 *
 * Sem a terceira, uma empresa convidada não consegue responder — e um
 * concurso com convites que ninguém pode responder é pior do que um concurso
 * vazio, porque parece que está a correr.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, Building2, X, Plus, ShieldCheck, Users, AlertTriangle, Check,
} from 'lucide-react';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';

const ESTADOS = {
  pending:   { txt: 'Por aprovar', cls: 'bg-amber-500/15 text-amber-400' },
  active:    { txt: 'Activa',      cls: 'bg-green-500/15 text-green-400' },
  suspended: { txt: 'Suspensa',    cls: 'bg-red-500/15 text-red-400' },
};

const kz = (v) => (v == null ? '—' : `${Number(v).toLocaleString('pt-PT')} Kz`);

const VAZIA = {
  nome: '', nif: '', alvara: '', telefone: '', email: '',
  endereco: '', representante: '', cargo: '', tipo: 'EMPRESA',
};

const dt = (v) => (v ? new Date(v).toLocaleDateString('pt-PT') : '—');

const EmpresasPrestadoras = () => {
  const { success, error } = useToast();
  const [lista, setLista] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [aberta, setAberta] = useState(null);
  const [capacidades, setCapacidades] = useState(null);
  const [membros, setMembros] = useState(null);
  const [tectos, setTectos] = useState({});
  const [novaCap, setNovaCap] = useState('');
  const [novoMembro, setNovoMembro] = useState('');
  const [nova, setNova] = useState(null);
  const [busy, setBusy] = useState(false);
  // A verificação do alvará: número, validade, classe e o que o verificador
  // reparou. Sem validade não há verificação — um título habilitante caduca.
  const [alv, setAlv] = useState({ alvara: '', validade: '', classe: '', observacoes: '' });

  const carregar = useCallback(() => {
    setLista(null);
    api.get('/operations/empresas-prestadoras')
      .then((r) => setLista(r.data || []))
      .catch(() => { setLista([]); error('Não foi possível carregar as empresas.'); });
  }, [error]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    api.get('/operations/categorias-obra').then((r) => setCategorias(r.data || [])).catch(() => {});
  }, []);

  const detalhe = useCallback(async (id) => {
    setCapacidades(null); setMembros(null);
    try {
      const [c, m] = await Promise.all([
        api.get(`/operations/empresas-prestadoras/${id}/capacidades`),
        api.get(`/operations/empresas-prestadoras/${id}/membros`),
      ]);
      setCapacidades(c.data || []);
      setMembros(m.data || []);
      setTectos(Object.fromEntries((c.data || []).map((x) => [x.capacidade_id, x.valor_maximo_kz ?? ''])));
    } catch { setCapacidades([]); setMembros([]); }
  }, []);

  const abrir = (e) => {
    setAberta(e); setNovaCap(''); setNovoMembro('');
    setAlv({
      alvara: e.alvara || '',
      validade: e.alvara_validade ? String(e.alvara_validade).slice(0, 10) : '',
      classe: e.alvara_classe || '',
      observacoes: e.alvara_observacoes || '',
    });
    detalhe(e.business_id);
  };

  // Caducado é diferente de por verificar: o primeiro já foi visto por alguém
  // e deixou de valer, o segundo nunca foi visto.
  const alvaraCaducado = (e) => !!e.alvara_validade
    && new Date(e.alvara_validade) < new Date(new Date().toDateString());

  const registar = async () => {
    setBusy(true);
    try {
      await api.post('/operations/empresas-prestadoras', nova);
      success('Empresa registada. Falta aprovar e verificar capacidades.');
      setNova(null); carregar();
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível registar.');
    } finally { setBusy(false); }
  };

  // O documento do alvará. Carrega-se antes de verificar: é o que se está a
  // conferir, e sem ele a verificação não tem nada por trás.
  const carregarAlvara = async (ficheiro) => {
    if (!ficheiro) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('ficheiro', ficheiro);
      const { data } = await api.post(
        `/operations/empresas-prestadoras/${aberta.business_id}/alvara/ficheiro`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      success('Documento do alvará carregado.');
      setAberta((a) => (a ? { ...a, ...data } : a));
      carregar();
    } catch (err) {
      error(err.response?.data?.message || 'Não foi possível carregar o documento.');
    } finally { setBusy(false); }
  };

  const verAlvara = async () => {
    try {
      const r = await api.get(
        `/operations/empresas-prestadoras/${aberta.business_id}/alvara/ficheiro`,
        { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch { error('Não foi possível abrir o documento.'); }
  };

  const verificarAlvara = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/operations/empresas-prestadoras/${aberta.business_id}/alvara`, alv);
      success('Alvará verificado. Fica registado quem verificou e quando.');
      setAberta((a) => (a ? { ...a, ...data } : a));
      carregar();
    } catch (err) {
      error(err.response?.data?.message || 'Não foi possível gravar a verificação.');
    } finally { setBusy(false); }
  };

  const mudarEstado = async (e, estado) => {
    setBusy(true);
    try {
      await api.put(`/operations/empresas-prestadoras/${e.business_id}/estado`, { estado });
      success(`${e.nome}: ${ESTADOS[estado]?.txt.toLowerCase()}.`);
      setAberta((a) => (a ? { ...a, estado } : a));
      carregar();
    } catch (err) {
      error(err.response?.data?.message || 'Não foi possível mudar o estado.');
    } finally { setBusy(false); }
  };

  const gravarCap = async (c, verificado) => {
    setBusy(true);
    try {
      await api.patch(`/operations/capacidades/${c.capacidade_id}`, {
        verificado, tecto_kz: tectos[c.capacidade_id],
      });
      success(verificado
        ? `${c.categoria_efectiva} verificada até ${kz(tectos[c.capacidade_id])}.`
        : `${c.categoria_efectiva} deixou de estar verificada.`);
      detalhe(aberta.business_id); carregar();
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível gravar.');
    } finally { setBusy(false); }
  };

  const criarCap = async () => {
    setBusy(true);
    try {
      await api.post(`/operations/empresas-prestadoras/${aberta.business_id}/capacidades`, { categoria: novaCap });
      success('Capacidade acrescentada. Falta verificá-la.');
      setNovaCap(''); detalhe(aberta.business_id); carregar();
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível acrescentar.');
    } finally { setBusy(false); }
  };

  const ligarMembro = async () => {
    setBusy(true);
    try {
      await api.post(`/operations/empresas-prestadoras/${aberta.business_id}/membros`,
        { user_id: Number(novoMembro) });
      success('Pessoa ligada à empresa.');
      setNovoMembro(''); detalhe(aberta.business_id); carregar();
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível ligar.');
    } finally { setBusy(false); }
  };

  /** O que falta a esta empresa para receber convites. */
  const emFalta = (e) => {
    const f = [];
    if (e.estado !== 'active') f.push('aprovação');
    if (!e.capacidades_verificadas) f.push('capacidade verificada');
    if (!e.membros) f.push('membro que responda');
    return f;
  };

  const campo = (k, rotulo, extra = {}) => (
    <div key={k}>
      <label htmlFor={`n-${k}`} className="block text-xs text-slate-400 mb-1">{rotulo}</label>
      <input id={`n-${k}`} value={nova[k]} {...extra}
        onChange={(ev) => setNova((n) => ({ ...n, [k]: ev.target.value }))}
        className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm outline-none focus:border-blue-500" />
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-blue-400" /> Empresas prestadoras
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Quem executa obras. Só recebe convites com aprovação, capacidade verificada e alguém que responda.
          </p>
        </div>
        <button onClick={() => setNova({ ...VAZIA })}
          className="min-h-[44px] px-4 rounded-lg bg-blue-600 text-white font-semibold inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Registar empresa
        </button>
      </div>

      {lista === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
      ) : lista.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium text-slate-400">Nenhuma empresa prestadora registada</p>
          <p className="text-sm mt-1">Sem empresas, as obras acima do limiar abrem concurso sem ninguém a convidar.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {lista.map((e) => {
            const s = ESTADOS[e.estado] || ESTADOS.pending;
            const falta = emFalta(e);
            return (
              <li key={e.business_id}>
                <button onClick={() => abrir(e)}
                  className="w-full text-left p-4 rounded-xl border border-slate-800 bg-slate-900/50 hover:bg-slate-900">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-semibold text-slate-100">{e.nome}</div>
                      <div className="text-xs text-slate-500 mt-0.5 font-mono">
                        NIF {e.nif || '—'}{e.alvara ? ` · Alvará ${e.alvara}` : ''}
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-md text-[11px] font-semibold ${s.cls}`}>{s.txt}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
                    <span>{e.capacidades_verificadas}/{e.capacidades} capacidades verificadas</span>
                    <span>{e.membros} {e.membros === 1 ? 'membro' : 'membros'}</span>
                    {falta.length > 0 && (
                      <span className="flex items-center gap-1 text-amber-400">
                        <AlertTriangle className="w-3 h-3" /> não recebe convites — falta {falta.join(', ')}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* ── Registar ── */}
      {nova && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => !busy && setNova(null)}>
          <div onClick={(ev) => ev.stopPropagation()}
            className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg p-5 my-8 space-y-3">
            <div className="flex items-start gap-3">
              <h2 className="text-lg font-bold text-white flex-1">Registar empresa prestadora</h2>
              <button onClick={() => setNova(null)} aria-label="Fechar"
                className="grid place-items-center w-11 h-11 rounded-xl bg-slate-800 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Se o NIF já existir na plataforma, a empresa não é duplicada: passa também a prestadora.
            </p>
            {campo('nome', 'Nome da empresa')}
            {campo('nif', 'NIF', { inputMode: 'numeric' })}
            {campo('alvara', 'Alvará (opcional)')}
            <div className="grid grid-cols-2 gap-3">
              {campo('representante', 'Representante')}
              {campo('cargo', 'Cargo')}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {campo('telefone', 'Telefone', { inputMode: 'tel' })}
              {campo('email', 'Email', { type: 'email' })}
            </div>
            <div>
              <label htmlFor="n-tipo" className="block text-xs text-slate-400 mb-1">Dimensão</label>
              <select id="n-tipo" value={nova.tipo}
                onChange={(ev) => setNova((n) => ({ ...n, tipo: ev.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm">
                <option value="EMPRESA">Empresa</option>
                <option value="EMPRESA_ESPECIALIZADA">Empresa especializada</option>
              </select>
            </div>
            <button onClick={registar} disabled={busy || !nova.nome.trim() || !nova.nif.trim()}
              className="w-full min-h-[44px] rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-40">
              {busy ? 'A registar…' : 'Registar'}
            </button>
          </div>
        </div>
      )}

      {/* ── Detalhe ── */}
      {aberta && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => !busy && setAberta(null)}>
          <div onClick={(ev) => ev.stopPropagation()}
            className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg p-5 my-8 space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-white">{aberta.nome}</h2>
                <p className="text-sm text-slate-500 font-mono">NIF {aberta.nif || '—'}</p>
              </div>
              <button onClick={() => setAberta(null)} aria-label="Fechar"
                className="grid place-items-center w-11 h-11 rounded-xl bg-slate-800 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <dl className="rounded-xl bg-slate-800/50 divide-y divide-slate-800">
              {[
                ['Estado', ESTADOS[aberta.estado]?.txt || aberta.estado],
                ['Alvará', aberta.alvara || '—'],
                ['Representante', aberta.representante ? `${aberta.representante}${aberta.cargo ? ` · ${aberta.cargo}` : ''}` : '—'],
                ['Contacto', [aberta.telefone, aberta.email].filter(Boolean).join(' · ') || '—'],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-3 px-3 py-2 text-sm">
                  <dt className="text-slate-500 w-32 shrink-0">{k}</dt>
                  <dd className="flex-1 min-w-0 break-words text-slate-200">{v}</dd>
                </div>
              ))}
            </dl>

            {/* Verificação do alvará.
                O contrato de empreitada diz que a Dozy verifica validade,
                classe e habilitações antes do início da execução. Enquanto isto
                não estiver registado, a obra não entra em execução. */}
            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 space-y-2">
              <div className="text-slate-400 text-xs uppercase tracking-wide font-semibold">
                Título habilitante
              </div>

              {aberta.alvara_verificado_em ? (
                <p className={`text-sm ${alvaraCaducado(aberta) ? 'text-red-400' : 'text-green-400'}`}>
                  {alvaraCaducado(aberta) ? 'Caducou' : 'Verificado'} ·{' '}
                  {aberta.alvara_classe ? `classe ${aberta.alvara_classe} · ` : ''}
                  válido até {dt(aberta.alvara_validade)}
                  <span className="text-slate-500"> (verificado em {dt(aberta.alvara_verificado_em)})</span>
                </p>
              ) : (
                <p className="flex items-start gap-1.5 text-xs text-amber-400">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  Por verificar. Confirme o documento à frente — validade, classe e se as
                  habilitações cobrem a natureza e o valor das obras que a empresa vai executar.
                </p>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <label className="min-h-[44px] px-3 inline-flex items-center rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-sm cursor-pointer">
                  {aberta.alvara_ficheiro ? 'Substituir documento' : 'Carregar foto ou PDF do alvará'}
                  <input type="file" accept="image/*,application/pdf" className="hidden"
                    onChange={(e) => carregarAlvara(e.target.files?.[0])} />
                </label>
                {aberta.alvara_ficheiro && (
                  <button type="button" onClick={verAlvara}
                    className="min-h-[44px] px-3 rounded-lg bg-slate-800 border border-slate-700 text-cyan-400 text-sm">
                    Ver documento
                  </button>
                )}
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <input value={alv.alvara} onChange={(e) => setAlv((a) => ({ ...a, alvara: e.target.value }))}
                  placeholder="N.º do alvará"
                  className="min-h-[44px] px-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200" />
                <input type="date" value={alv.validade}
                  onChange={(e) => setAlv((a) => ({ ...a, validade: e.target.value }))}
                  className="min-h-[44px] px-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200" />
                <input value={alv.classe} onChange={(e) => setAlv((a) => ({ ...a, classe: e.target.value }))}
                  placeholder="Classe / habilitações"
                  className="min-h-[44px] px-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200" />
                <input value={alv.observacoes} onChange={(e) => setAlv((a) => ({ ...a, observacoes: e.target.value }))}
                  placeholder="Observações (opcional)"
                  className="min-h-[44px] px-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200" />
              </div>

              <button onClick={verificarAlvara} disabled={busy || !alv.validade}
                className="w-full min-h-[44px] rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-40">
                Registar verificação do alvará
              </button>
            </div>

            <div className="flex gap-2">
              {aberta.estado !== 'active' && (
                <button onClick={() => mudarEstado(aberta, 'active')} disabled={busy}
                  className="flex-1 min-h-[44px] rounded-lg bg-green-600 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40">
                  <Check className="w-4 h-4" /> Aprovar
                </button>
              )}
              {aberta.estado === 'active' && (
                <button onClick={() => mudarEstado(aberta, 'suspended')} disabled={busy}
                  className="flex-1 min-h-[44px] rounded-lg bg-slate-800 text-red-400 font-medium">
                  Suspender
                </button>
              )}
            </div>

            {/* ── Capacidades ── */}
            <div>
              <h3 className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                <ShieldCheck className="w-3.5 h-3.5" /> Capacidades
              </h3>
              {capacidades === null ? (
                <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-600" /></div>
              ) : (
                <>
                  {capacidades.length === 0 && (
                    <p className="text-xs text-slate-500 mb-2">Nenhuma — esta empresa não recebe concursos.</p>
                  )}
                  <ul className="space-y-2">
                    {capacidades.map((c) => (
                      <li key={c.capacidade_id} className="p-3 rounded-xl border border-slate-800 bg-slate-950">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-slate-200 text-sm">{c.categoria_efectiva || '—'}</span>
                          <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                            c.verificado ? 'bg-green-500/15 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
                            {c.verificado ? 'Verificada' : 'Por verificar'}
                          </span>
                          {c.verificado && (
                            <span className="ml-auto text-xs text-slate-500 tabular-nums">até {kz(c.valor_maximo_kz)}</span>
                          )}
                        </div>
                        <div className="flex gap-2 items-end">
                          <div className="flex-1">
                            <label htmlFor={`t-${c.capacidade_id}`} className="block text-[11px] text-slate-500 mb-1">
                              Tecto de obra (Kz)
                            </label>
                            <input id={`t-${c.capacidade_id}`} type="number" min="1" inputMode="numeric"
                              value={tectos[c.capacidade_id] ?? ''}
                              onChange={(ev) => setTectos((t) => ({ ...t, [c.capacidade_id]: ev.target.value }))}
                              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm tabular-nums outline-none focus:border-blue-500" />
                          </div>
                          <button onClick={() => gravarCap(c, !c.verificado)}
                            disabled={busy || (!c.verificado && !String(tectos[c.capacidade_id] || '').trim())}
                            className={`h-[38px] px-4 rounded-lg text-sm font-semibold disabled:opacity-40 ${
                              c.verificado ? 'bg-slate-800 text-amber-400' : 'bg-green-600 text-white'}`}>
                            {c.verificado ? 'Retirar' : 'Verificar'}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-2 mt-2">
                    <select value={novaCap} onChange={(ev) => setNovaCap(ev.target.value)}
                      aria-label="Categoria de obra a acrescentar"
                      className="flex-1 px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm">
                      <option value="">Acrescentar categoria…</option>
                      {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button onClick={criarCap} disabled={busy || !novaCap}
                      className="px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-40">
                      Acrescentar
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* ── Membros ── */}
            <div>
              <h3 className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                <Users className="w-3.5 h-3.5" /> Quem responde pela empresa
              </h3>
              {membros === null ? (
                <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-600" /></div>
              ) : (
                <>
                  {membros.length === 0 ? (
                    <p className="flex items-start gap-1.5 text-xs text-amber-400 mb-2">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                      Ninguém. A empresa pode ser convidada, mas não consegue responder.
                    </p>
                  ) : (
                    <ul className="space-y-1 mb-2">
                      {membros.map((m) => (
                        <li key={m.user_id} className="flex items-baseline gap-2 text-sm text-slate-300">
                          <span>{m.full_name}</span>
                          <span className="text-xs text-slate-600">#{m.user_id} · {m.papel}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex gap-2">
                    <input value={novoMembro} onChange={(ev) => setNovoMembro(ev.target.value)}
                      type="number" inputMode="numeric" placeholder="ID do utilizador"
                      aria-label="ID do utilizador a ligar"
                      className="flex-1 px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm tabular-nums outline-none focus:border-blue-500" />
                    <button onClick={ligarMembro} disabled={busy || !novoMembro}
                      className="px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-40">
                      Ligar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmpresasPrestadoras;
